/**
 * DrugInteractionsChartTab — D/I (Drug Interactions) inner tab of the
 * patient Chart shell (PROD-737 PR B, read-only).
 *
 * Flutter ref: `patient_chart/.../tab_widgets/drugInt_tab.dart`.
 *
 * Surfaces persisted interaction alerts attached to the patient's
 * active medications. The list is COMPUTED SERVER-SIDE — there is no
 * client-side cross-referencing of meds + allergies. When a doctor
 * drafts a new Rx, the backend's `PrescriptionAlert` endpoint runs
 * the interaction engine and persists alerts that flow into this view.
 *
 * **No-alerts is the desirable state.** Empty here means the patient's
 * current medication regimen has zero flagged interactions — render a
 * positive (green-tinted) confirmation card rather than a generic
 * em-dash empty state.
 *
 * Tombstone filter is loose because the wire types `isDeleted` as
 * `dynamic` (Flutter) — backend may ship null/true/false/"false"
 * interchangeably. Strict `!== true` is the safe predicate either way.
 */

import { useReportChartCount } from './chart-page-context';
import { ChartShowAll } from './chart-row-cap';
import { useRowCap } from './use-row-cap';
import { StatusChip } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { usePatientDrugInteractions } from '@/patients/services/use-patient-drug-interactions';
import { ChartSectionHeader } from './chart-section-header';
import type { PatientDrugInteraction } from '@/types/patient-chart-api';

export interface DrugInteractionsChartTabProps {
  patientId: string | null | undefined;
}

export function DrugInteractionsChartTab({
  patientId,
}: DrugInteractionsChartTabProps) {
  const { data, isLoading, isError, refetch } = usePatientDrugInteractions(patientId);
  const rows = (data ?? []).filter((r) => r.isDeleted !== true);
  // Sort by severity: most severe first.
  const sorted = [...rows].sort((a, b) => severityRank(b.severity) - severityRank(a.severity));
  useReportChartCount(isLoading ? null : rows.length);
  const cap = useRowCap(sorted);

  if (!patientId) {
    return (
      <div className="p-6">
        <EmptyCard>Save the patient first to check for drug interactions.</EmptyCard>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 di-stack">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        {/* The design's error (PaDrugInteractions .empty): the dashed card and its line; Retry kept as a link under it. */}
        <EmptyCard>
          Could not load drug interactions. Try again later.
          <button type="button" className="di-retry" onClick={() => void refetch()}>
            Retry
          </button>
        </EmptyCard>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="p-6">
        {/* No alerts is the good state: one quiet line, no action (alerts come from the Rx engine), the design's .none. */}
        <p className="di-none">No drug interactions detected.</p>
      </div>
    );
  }

  return (
    <div className="p-6 di-stack">
      {/* Drug-interaction alerts are computed server-side off the Rx engine —
       *  there's no chart-level add. */}
      <ChartSectionHeader title="Drug interactions" count={rows.length} showAdd={false} />
      {cap.visible.map((r) => (
        <AlertCard
          key={r.id ?? `${r.patientRxId}-${r.triggeredBy}-${r.title}`}
          row={r}
        />
      ))}
      <ChartShowAll cap={cap} label="drug interactions" />
    </div>
  );
}

function AlertCard({ row }: { row: PatientDrugInteraction }) {
  const sev = (row.severity ?? '').toLowerCase();
  const isSevere = sev.includes('severe') || sev.includes('high');
  const isModerate = sev.includes('moderate');
  // Safety: DI alert intentionally retains this severity-tinted card border —
  // it's container styling (not a status chip), so do NOT migrate it to
  // StatusChip in a future sweep. The severity *chip* inside uses StatusChip.
  const tone = isSevere ? 'error' : isModerate ? 'warning' : 'info';
  return (
    <div className="di-card" data-tone={tone}>
      <div className="di-top">
        <SeverityBadge severity={row.severity} />
        {row.alertType ? <span className="di-type">{row.alertType}</span> : null}
      </div>
      <h3 className="di-title">
        {row.title?.trim() ||
          row.medicationname?.trim() ||
          'Drug interaction'}
      </h3>
      {row.description?.trim() ? <p className="di-desc">{row.description}</p> : null}
      <div className="di-fields">
        <Field label="Medication" value={row.medicationname} />
        <Field label="Triggered by" value={row.triggeredBy} />
        <Field label="Triggered type" value={row.triggeredType} />
        <Field label="Detail" value={row.triggeredDetail} />
      </div>
    </div>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="di-field">
      <div className="di-label">{label}</div>
      <div className="di-value">{value?.trim() || '—'}</div>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: string | null | undefined }) {
  if (!severity?.trim()) return null;
  const sev = severity.toLowerCase();
  // Drug-interaction severity keeps the SOLID (saturated) tone — it's a
  // safety-critical signal that's deliberately stronger than the soft chips
  // elsewhere. severe/high → error, moderate → warning, mild/low → info.
  const semantic = sev.includes('severe') || sev.includes('high')
    ? 'error'
    : sev.includes('moderate')
      ? 'warning'
      : sev.includes('mild') || sev.includes('low')
        ? 'info'
        : 'neutral';
  return (
    <StatusChip tone="solid" semantic={semantic}>
      {severity}
    </StatusChip>
  );
}

function severityRank(severity: string | null | undefined): number {
  if (!severity) return 0;
  const s = severity.toLowerCase();
  if (s.includes('severe') || s.includes('high')) return 3;
  if (s.includes('moderate')) return 2;
  if (s.includes('mild') || s.includes('low')) return 1;
  return 0;
}

function EmptyCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="di-empty">{children}</div>
  );
}
